import { toBlob } from 'html-to-image';

const TOAST_Z = 10000;
const LOADING_TOAST_ID = 'citius-screenshot-toast-loading';
const SUCCESS_TOAST_ID = 'citius-screenshot-toast-success';
const ERROR_TOAST_ID = 'citius-screenshot-toast-error';

const SELECTOR_APP_ROOT = '.citius-screenshot-app-root';
const SELECTOR_DASHBOARD_PAGE = '.citius-screenshot-dashboard-page';
const SELECTOR_GRID_WRAP = '.citius-dashboard-grid';

const EXPORT_STYLE_ID = 'citius-screenshot-export-overrides';

/** 1×1 transparent PNG — used when an image (e.g. map tile) cannot be inlined under CSP/CORS. */
const IMAGE_PLACEHOLDER_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';

/** Applied to the live document during export (html-to-image clones computed styles; no html2canvas onclone). */
const SCREENSHOT_EXPORT_CSS = `
  [data-screenshot-mode="true"], .screenshot-mode {
    visibility: visible !important;
    opacity: 1 !important;
    animation: none !important;
    transition: none !important;
  }
  .react-grid-layout,
  .react-grid-layout * {
    box-shadow: none !important;
    outline: none !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }
  .citius-screenshot-header-chrome,
  .citius-screenshot-header-chrome * {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    box-shadow: none !important;
  }
  .citius-training-log-dock,
  .citius-training-log-dock * {
    box-shadow: none !important;
    outline: none !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }
  .citius-widget-capture-shell {
    background-color: hsl(var(--card)) !important;
    opacity: 1 !important;
  }
  .citius-screenshot-header-chrome button,
  .citius-screenshot-header-chrome [role="button"] {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .citius-screenshot-header-chrome .flex.items-center,
  .citius-screenshot-header-chrome .inline-flex.items-center {
    align-items: center !important;
  }
  .citius-screenshot-header-chrome > div.flex.h-16.items-center > * {
    align-self: center !important;
  }
  .citius-screenshot-header-chrome span.inline-flex {
    display: inline-flex !important;
    align-items: center !important;
  }
  .citius-screenshot-header-chrome button svg,
  .citius-screenshot-header-chrome a svg {
    display: block !important;
    flex-shrink: 0 !important;
  }
  .citius-screenshot-avatar-initials {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 !important;
  }
  .react-grid-layout div.inline-flex.rounded-full {
    align-items: center !important;
    justify-content: center !important;
  }
  .react-grid-layout svg.lucide {
    display: block !important;
    flex-shrink: 0 !important;
  }
`;

const DEFAULT_MESSAGES = {
  capturing: 'Capturing screenshot…',
  success: 'Screenshot saved',
  error: 'Screenshot failed',
};

const widgetPrepState = [];

function mergeMessages(overrides) {
  return { ...DEFAULT_MESSAGES, ...(overrides || {}) };
}

export function shouldIgnoreScreenshotElement(el) {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (tag === 'SCRIPT' || tag === 'NOSCRIPT') return true;
  if (el.classList?.contains('screenshot-ignore')) return true;
  if (el.classList?.contains('screenshot-dropdown')) return true;
  if (el.classList?.contains('screenshot-options')) return true;
  if (el.classList?.contains('screenshot-overlay')) return true;
  const id = el.id || '';
  if (/toast/i.test(id)) return true;
  const cls = typeof el.className === 'string' ? el.className : el.className?.baseVal || '';
  if (/toast|notification/i.test(cls)) return true;
  return false;
}

/** html-to-image: return true to include node (root is always included; filter not applied to root). */
function htmlToImageFilter(node) {
  if (!node || node.nodeType !== 1) return true;
  return !shouldIgnoreScreenshotElement(node);
}

function pushLiveExportStyles() {
  document.getElementById(EXPORT_STYLE_ID)?.remove();
  const style = document.createElement('style');
  style.id = EXPORT_STYLE_ID;
  style.textContent = SCREENSHOT_EXPORT_CSS;
  document.head.appendChild(style);
}

function popLiveExportStyles() {
  document.getElementById(EXPORT_STYLE_ID)?.remove();
}

function exportBackgroundColor() {
  try {
    const bg = getComputedStyle(document.body).backgroundColor;
    if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return bg;
  } catch {
    /* ignore */
  }
  return 'hsl(var(--background))';
}

function baseToImageOptions(overrides = {}) {
  return {
    cacheBust: true,
    filter: htmlToImageFilter,
    pixelRatio: 1,
    backgroundColor: exportBackgroundColor(),
    /*
      skipFonts: html-to-image otherwise fetches @font-face CSS (e.g. fonts.googleapis.com), which
      strict prod CSP connect-src blocks — capture would fail. Fonts are already loaded for the UI;
      the export may use fallbacks inside the SVG snapshot where embed is skipped.
    */
    skipFonts: true,
    imagePlaceholder: IMAGE_PLACEHOLDER_DATA_URL,
    ...overrides,
  };
}

function removeToast(id) {
  document.getElementById(id)?.remove();
}

export function showLoadingToast(messages) {
  removeToast(LOADING_TOAST_ID);
  const m = mergeMessages(messages);
  const div = document.createElement('div');
  div.id = LOADING_TOAST_ID;
  div.className = 'screenshot-ignore';
  div.setAttribute('role', 'status');
  div.style.cssText = [
    `position:fixed`,
    `left:50%`,
    `top:24px`,
    `transform:translateX(-50%)`,
    `z-index:${TOAST_Z}`,
    `padding:10px 16px`,
    `border-radius:8px`,
    `background:hsl(var(--card))`,
    `color:hsl(var(--card-foreground))`,
    `border:1px solid hsl(var(--border))`,
    `box-shadow:0 4px 24px rgba(0,0,0,0.12)`,
    `font-size:14px`,
    `display:flex`,
    `align-items:center`,
    `gap:8px`,
    `pointer-events:none`,
  ].join(';');
  div.innerHTML = `<span style="display:inline-block;width:14px;height:14px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:spin .6s linear infinite"></span><span>${escapeHtml(m.capturing)}</span>`;
  if (!document.getElementById('citius-screenshot-spin-style')) {
    const st = document.createElement('style');
    st.id = 'citius-screenshot-spin-style';
    st.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(st);
  }
  document.body.appendChild(div);
}

export function hideLoadingToast() {
  removeToast(LOADING_TOAST_ID);
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function showSuccessToast(messages) {
  removeToast(SUCCESS_TOAST_ID);
  const m = mergeMessages(messages);
  const div = document.createElement('div');
  div.id = SUCCESS_TOAST_ID;
  div.className = 'screenshot-ignore';
  div.style.cssText = [
    `position:fixed`,
    `left:50%`,
    `bottom:24px`,
    `transform:translateX(-50%)`,
    `z-index:${TOAST_Z}`,
    `padding:10px 16px`,
    `border-radius:8px`,
    `background:hsl(var(--primary))`,
    `color:hsl(var(--primary-foreground))`,
    `font-size:14px`,
    `pointer-events:none`,
  ].join(';');
  div.textContent = m.success;
  document.body.appendChild(div);
  setTimeout(() => removeToast(SUCCESS_TOAST_ID), 2800);
}

export function showErrorToast(messages) {
  removeToast(ERROR_TOAST_ID);
  const m = mergeMessages(messages);
  const div = document.createElement('div');
  div.id = ERROR_TOAST_ID;
  div.className = 'screenshot-ignore';
  div.style.cssText = [
    `position:fixed`,
    `left:50%`,
    `bottom:24px`,
    `transform:translateX(-50%)`,
    `z-index:${TOAST_Z}`,
    `padding:10px 16px`,
    `border-radius:8px`,
    `background:hsl(var(--destructive))`,
    `color:hsl(var(--destructive-foreground))`,
    `font-size:14px`,
    `pointer-events:none`,
    `max-width:min(90vw,360px)`,
    `text-align:center`,
  ].join(';');
  div.textContent = m.error;
  document.body.appendChild(div);
  setTimeout(() => removeToast(ERROR_TOAST_ID), 4200);
}

function downloadBlob(blob, filename) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fullPageHeight() {
  const b = document.body;
  const e = document.documentElement;
  return Math.max(
    b.scrollHeight,
    b.offsetHeight,
    e.clientHeight,
    e.scrollHeight,
    e.offsetHeight
  );
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export function hasDashboardContent() {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname.replace(/\/$/, '') || '/';
  if (p === '/dashboard') return true;
  return !!document.querySelector(
    `${SELECTOR_DASHBOARD_PAGE}, ${SELECTOR_GRID_WRAP}, .react-grid-layout, .react-grid-item`
  );
}

export function prepareWidgetsForCapture() {
  widgetPrepState.length = 0;
  document.querySelectorAll('.react-grid-item').forEach((el) => {
    widgetPrepState.push({
      el,
      transform: el.style.transform,
      visibility: el.style.visibility,
      opacity: el.style.opacity,
      transition: el.style.transition,
      animation: el.style.animation,
    });
    el.setAttribute('data-screenshot-mode', 'true');
    el.style.visibility = 'visible';
    el.style.opacity = '1';
    el.style.transition = 'none';
    el.style.animation = 'none';
  });
  window.dispatchEvent(new CustomEvent('intersection-visible'));
}

export function restoreWidgetsAfterCapture() {
  widgetPrepState.forEach(
    ({ el, transform, visibility, opacity, transition, animation }) => {
      el.removeAttribute('data-screenshot-mode');
      el.style.transform = transform;
      el.style.visibility = visibility;
      el.style.opacity = opacity;
      el.style.transition = transition;
      el.style.animation = animation;
    }
  );
  widgetPrepState.length = 0;
}

function temporarilyHideSelectors(selectors) {
  const entries = [];
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      entries.push({ el, display: el.style.display, visibility: el.style.visibility });
      el.style.display = 'none';
      el.style.visibility = 'hidden';
    });
  });
  return () => {
    entries.forEach(({ el, display, visibility }) => {
      el.style.display = display;
      el.style.visibility = visibility;
    });
  };
}

async function waitForWidgetSettle(maxMs = 2000) {
  const start = Date.now();
  await delay(400);
  while (Date.now() - start < maxMs) {
    const pending = [...document.querySelectorAll('.react-grid-item')].some((el) => {
      const img = el.querySelector('img[src]');
      return img && !img.complete;
    });
    if (!pending) break;
    await delay(120);
  }
  await delay(200);
}

/**
 * @param {object} options
 * @param {HTMLElement} [options.element]
 * @param {string} [options.filename]
 * @param {object} [options.html2canvasOptions] legacy: use `{ scale }` or `{ pixelRatio }` for export density
 * @param {object} [options.messages] i18n strings: capturing, success, error
 * @param {boolean} [options.flattenGrid] no-op (grid uses transforms; html-to-image preserves layout)
 * @param {boolean} [options.resetFixedInClone] no-op
 */
export async function capturePageAsPNG(options = {}) {
  const {
    element = document.body,
    filename = `citius-screenshot-${Date.now()}.png`,
    html2canvasOptions = {},
    messages,
  } = options;

  const pixelRatio = html2canvasOptions.pixelRatio ?? html2canvasOptions.scale ?? 1;

  prepareWidgetsForCapture();
  await waitForWidgetSettle(1500);

  pushLiveExportStyles();
  try {
    const blob = await toBlob(
      element,
      baseToImageOptions({
        pixelRatio,
        width: element.scrollWidth,
        height: element.scrollHeight,
      })
    );
    downloadBlob(blob, filename);
    showSuccessToast(messages);
  } catch (e) {
    console.error('capturePageAsPNG', e);
    showErrorToast(messages);
    throw e;
  } finally {
    popLiveExportStyles();
    restoreWidgetsAfterCapture();
  }
}

export async function captureDashboardSection(selector, filename, messages) {
  const el = document.querySelector(selector);
  if (!el) {
    showErrorToast(messages);
    throw new Error(`Element not found: ${selector}`);
  }
  prepareWidgetsForCapture();
  await waitForWidgetSettle(1500);
  pushLiveExportStyles();
  try {
    const blob = await toBlob(
      el,
      baseToImageOptions({
        width: el.scrollWidth,
        height: el.scrollHeight,
      })
    );
    downloadBlob(blob, filename || `citius-screenshot-${Date.now()}.png`);
    showSuccessToast(messages);
  } catch (e) {
    console.error('captureDashboardSection', e);
    showErrorToast(messages);
    throw e;
  } finally {
    popLiveExportStyles();
    restoreWidgetsAfterCapture();
  }
}

export async function captureFullDashboard(messages) {
  const filename = `citius-full-${Date.now()}.png`;
  const h = fullPageHeight();
  const w = window.innerWidth;
  const prevX = window.scrollX;
  const prevY = window.scrollY;
  prepareWidgetsForCapture();
  await waitForWidgetSettle(2000);
  window.scrollTo(0, 0);
  pushLiveExportStyles();
  try {
    const blob = await toBlob(
      document.body,
      baseToImageOptions({
        width: w,
        height: h,
      })
    );
    downloadBlob(blob, filename);
    showSuccessToast(messages);
  } catch (e) {
    console.error('captureFullDashboard', e);
    showErrorToast(messages);
    throw e;
  } finally {
    window.scrollTo(prevX, prevY);
    popLiveExportStyles();
    restoreWidgetsAfterCapture();
  }
}

export async function captureVisibleArea(messages) {
  const filename = `citius-visible-${Date.now()}.png`;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const sx = window.scrollX;
  const sy = window.scrollY;
  prepareWidgetsForCapture();
  await waitForWidgetSettle(1200);
  pushLiveExportStyles();
  try {
    const blob = await toBlob(
      document.body,
      baseToImageOptions({
        width: vw,
        height: vh,
        style: {
          transform: `translate(${-sx}px, ${-sy}px)`,
        },
      })
    );
    downloadBlob(blob, filename);
    showSuccessToast(messages);
  } catch (e) {
    console.error('captureVisibleArea', e);
    showErrorToast(messages);
    throw e;
  } finally {
    popLiveExportStyles();
    restoreWidgetsAfterCapture();
  }
}

/**
 * App shell: header + banner + main (excludes modals outside root). Includes training log dock.
 */
export async function captureDashboardContent(messages) {
  const root = document.querySelector(SELECTOR_APP_ROOT);
  if (!root) {
    await capturePageAsPNG({ messages });
    return;
  }
  const restoreLib = temporarilyHideSelectors(['.citius-widget-library']);
  const filename = `citius-dashboard-${Date.now()}.png`;
  const sx = window.scrollX;
  const sy = window.scrollY;
  prepareWidgetsForCapture();
  await waitForWidgetSettle(2000);
  pushLiveExportStyles();
  try {
    const blob = await toBlob(
      root,
      baseToImageOptions({
        width: root.scrollWidth,
        height: Math.max(root.scrollHeight, fullPageHeight()),
        style: {
          transform: `translate(${-sx}px, ${-sy}px)`,
        },
      })
    );
    downloadBlob(blob, filename);
    showSuccessToast(messages);
  } catch (e) {
    console.error('captureDashboardContent', e);
    showErrorToast(messages);
    throw e;
  } finally {
    restoreLib();
    popLiveExportStyles();
    restoreWidgetsAfterCapture();
  }
}

/**
 * Grid + widgets only; excludes training log dock and widget library.
 */
export async function captureWidgetsOnly(messages) {
  const wrap =
    document.querySelector(SELECTOR_GRID_WRAP) ||
    document.querySelector(`${SELECTOR_DASHBOARD_PAGE} .react-grid-layout`)?.parentElement;
  const gridEl =
    document.querySelector(`${SELECTOR_GRID_WRAP} .react-grid-layout`) ||
    document.querySelector(`${SELECTOR_DASHBOARD_PAGE} .react-grid-layout`) ||
    document.querySelector('.react-grid-layout');
  const target = wrap || gridEl;
  if (!target || !gridEl) {
    showErrorToast(messages);
    throw new Error('Dashboard grid not found');
  }
  const restoreHidden = temporarilyHideSelectors([
    '.citius-widget-library',
    '.citius-training-log-dock',
  ]);
  prepareWidgetsForCapture();
  await waitForWidgetSettle(2000);
  pushLiveExportStyles();
  try {
    const blob = await toBlob(
      target,
      baseToImageOptions({
        width: target.scrollWidth,
        height: target.scrollHeight,
      })
    );
    downloadBlob(blob, `citius-widgets-${Date.now()}.png`);
    showSuccessToast(messages);
  } catch (e) {
    console.error('captureWidgetsOnly', e);
    showErrorToast(messages);
    throw e;
  } finally {
    restoreHidden();
    popLiveExportStyles();
    restoreWidgetsAfterCapture();
  }
}

const ScreenshotUtils = {
  hasDashboardContent,
  capturePageAsPNG,
  captureDashboardSection,
  captureFullDashboard,
  captureVisibleArea,
  captureDashboardContent,
  captureWidgetsOnly,
  prepareWidgetsForCapture,
  restoreWidgetsAfterCapture,
  showLoadingToast,
  hideLoadingToast,
  showSuccessToast,
  showErrorToast,
  shouldIgnoreScreenshotElement,
};

export default ScreenshotUtils;
